import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'

import { useAppSelector } from '@/stores/hooks.ts'
import { acquiringPaymentSelectors } from '@/stores/slices/acquiring-payments-slice.ts'

import { AcqPayment, Nullable, RefundOperationParams, TableData } from '../types.ts'
import { omitRequestParams } from '../utils.ts'

import { apiService } from './root.ts'

export const ACQ_ROOT = '/acq-operations'

export const ACQ_ENDPOINTS = {
  list: ACQ_ROOT,
  item: (id: string) => `${ACQ_ROOT}/${id}`,
  reverse: (id: string) => `${ACQ_ROOT}/${id}/reverse`,
  reverseConfirm: `${ACQ_ROOT}/reverseConfirm`,
  xlsx: `${ACQ_ROOT}/xlsx`,
}

export const useGetAcqPayments = () => {
  const { page, showCount } = useAppSelector(acquiringPaymentSelectors.filters)
  const { state, clientFio, dateFrom, dateTo } = useAppSelector(acquiringPaymentSelectors.paramsForRequest)
  const [sortField, direction] = useAppSelector((state) => state.acquiringPayments.sort) || []

  const queryParams = omitRequestParams({
    page,
    showCount,
    state: state === 'ALL' ? undefined : state,
    clientFio,
    dateFrom,
    dateTo,
    sortField,
    direction,
  })

  return useQuery({
    placeholderData: keepPreviousData,
    queryKey: ['aqc_list', queryParams],
    queryFn: () =>
      apiService.get<TableData<AcqPayment>>(ACQ_ENDPOINTS.list, {
        queryParams,
      }),
  })
}

export const useGetAcqPayment = (id: string) =>
  useQuery({
    queryKey: ['acq-item', { id }],
    queryFn: () => apiService.get<AcqPayment>(ACQ_ENDPOINTS.item(id)),
  })

export type AcqRefundResponse =
  | {
      state: 'ERROR'
      message: Nullable<string>
    }
  | { state: 'OK'; message: Nullable<string> }

export const useMutateAqcRefund = (paymentId: string) =>
  useMutation({
    mutationFn: (bodyParams: RefundOperationParams) =>
      apiService.post<AcqRefundResponse>(ACQ_ENDPOINTS.reverse(paymentId), {
        bodyParams,
      }),
  })
